import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, Play, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import { listOffline, removeOffline } from "@/lib/offline";

const mb = (b) => `${(b / 1048576).toFixed(1)} MB`;

export default function Downloads() {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [urls, setUrls] = useState({});

  const load = () => listOffline().then(list => {
    const sorted = (list || []).sort((a, b) => b.saved_at.localeCompare(a.saved_at));
    setItems(sorted);
    setUrls(Object.fromEntries(sorted.map(i => [i.id, i.thumb ? URL.createObjectURL(i.thumb) : ""])));
  }).catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const remove = async (id) => { await removeOffline(id); toast.success("Removed from downloads"); load(); };
  const play = (it) => setPlaying({ ...it, src: URL.createObjectURL(it.blob) });
  const stop = () => { if (playing) URL.revokeObjectURL(playing.src); setPlaying(null); };

  return (
    <div className="mx-auto max-w-5xl px-3 sm:px-6 pt-6" data-testid="downloads-page">
      <h1 className="h-font text-3xl sm:text-4xl font-extrabold flex items-center gap-3"><Download size={26} className="text-[#00E7FF]"/> Downloads</h1>
      <p className="text-white/60 text-sm mt-1 flex items-center gap-1.5"><WifiOff size={14}/> Saved on this device — watch anytime, even offline.</p>

      {items.length === 0 && (
        <div className="mt-8 p-8 rounded-2xl bg-white/5 border border-white/10 text-center" data-testid="downloads-empty">
          <div className="h-font text-xl font-bold">No downloads yet</div>
          <p className="text-white/60 text-sm mt-1">Tap <b>Download</b> on any video to save it for offline viewing.</p>
          <Link to="/" className="jx-btn inline-block mt-4 h-11 leading-[2.75rem] px-6 rounded-full bg-[#00E7FF] text-black font-extrabold">Browse videos</Link>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="downloads-list">
        {items.map(it => (
          <div key={it.id} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10" data-testid={`download-item-${it.id}`}>
            <button onClick={() => play(it)} data-testid={`download-play-${it.id}`} className="relative block w-full aspect-video bg-black group">
              {urls[it.id] && <img src={urls[it.id]} alt={it.title} className="w-full h-full object-cover"/>}
              <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                <span className="w-12 h-12 rounded-full bg-[#00E7FF] flex items-center justify-center"><Play size={20} className="text-black ml-0.5"/></span>
              </span>
            </button>
            <div className="p-3 flex gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-2">{it.title}</div>
                <div className="text-xs text-white/50 mt-0.5">@{it.creator?.username} · {mb(it.size)} · <span data-testid={`download-quality-${it.id}`} className={it.quality === "sd" ? "text-[#FFD700]" : "text-[#00E7FF]"}>{it.quality === "sd" ? "Data saver 480p" : "Full quality"}</span></div>
              </div>
              <button onClick={() => remove(it.id)} data-testid={`download-remove-${it.id}`} className="jx-btn w-9 h-9 rounded-full bg-white/5 hover:bg-[#FF3EA5]/20 flex items-center justify-center shrink-0"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {playing && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-3" data-testid="offline-player">
          <button onClick={stop} data-testid="offline-player-close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X size={18}/></button>
          <video src={playing.src} controls autoPlay playsInline className="w-full max-w-5xl max-h-[80vh] rounded-xl bg-black"/>
          <div className="mt-3 font-bold text-center">{playing.title}</div>
        </div>
      )}
    </div>
  );
}
